import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import { Button } from "./ui/button";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";

const DataTable = ({ data, onError }) => {
  console.log('DataTable received data:', data);
  const [sorting, setSorting] = React.useState([]);

  // Create columns based on headers
  const columns = React.useMemo(() => {
    try {
      if (!data?.headers) return [];
      return data.headers.map(header => ({
        accessorKey: header,
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            >
              {header}
              {{
                asc: <ChevronUp className="ml-2 h-4 w-4" />,
                desc: <ChevronDown className="ml-2 h-4 w-4" />,
                false: <ChevronsUpDown className="ml-2 h-4 w-4" />,
              }[column.getIsSorted()] ?? null}
            </Button>
          );
        },
        cell: info => {
          const value = info.getValue();
          // Format based on column type
          const type = data.metadata?.columnTypes?.[header];
          
          if (value === null || value === undefined) return '-';
          
          switch (type) {
            case 'float':
            case 'Float':
              return Number(value).toFixed(2);
            case 'datetime':
            case 'DateTime':
              return new Date(value).toLocaleString();
            default:
              return String(value);
          }
        },
      }));
    } catch (error) {
      console.error('Error creating columns:', error);
      onError?.(error);
      return [];
    }
  }, [data]);

  const table = useReactTable({
    data: data?.rows ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  if (!data) return null;

  try {
    return (
      <div className="rounded-md border bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
        <div className="relative overflow-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between p-4 border-t">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error rendering DataTable:', error);
    onError?.(error);
    return (
      <div className="text-red-500 p-4">
        Error rendering table: {error.message}
      </div>
    );
  }
};

export default DataTable; 